import React from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Box, Text, useColorModeValue } from "@chakra-ui/react"
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react"
import { useDndContext } from "@dnd-kit/core"

import { useIsBelowLg } from "@/hooks/use-mobile"

interface SortableCardProps {
  id: string
  children: React.ReactNode
  items?: string[]
  editMode?: boolean
  canMoveUp?: boolean
  canMoveDown?: boolean
  onMoveUp?: () => void
  onMoveDown?: () => void
  /** When a parent renders a half-row merge preview, skip the full-card drop overlay. */
  suppressDropPlaceholder?: boolean
  /** Keep this card stationary while another sortable item is dragged (chart-row pairing). */
  suppressTransform?: boolean
  /** Disable sortable layout shift animations (chart-row pairing). */
  disableLayoutAnimation?: boolean
  /** Stretch to the tallest sibling in a split chart row. */
  fillHeight?: boolean
}

const SortableCard: React.FC<SortableCardProps> = ({
  id,
  children,
  editMode = false,
  canMoveUp = false,
  canMoveDown = false,
  onMoveUp,
  onMoveDown,
  suppressDropPlaceholder = false,
  suppressTransform = false,
  disableLayoutAnimation = false,
  fillHeight = false,
}) => {
  const isBelowLg = useIsBelowLg()
  const showMoveArrows = editMode && isBelowLg && (onMoveUp != null || onMoveDown != null)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id,
    animateLayoutChanges: disableLayoutAnimation ? () => false : undefined,
  })

  const { active, over } = useDndContext()
  const isDraggingOther = active && active.id !== id

  let showPlaceholder = false

  if (isDraggingOther && active) {
    const activeId = active.id as string

    if (over && over.id === id) {
      showPlaceholder = true
    } else if (!over && id === activeId) {
      showPlaceholder = true
    }
  }

  showPlaceholder = showPlaceholder && !isDragging && !suppressDropPlaceholder && !suppressTransform

  const borderColor = "#D3D3D3"
  const dragHandleBg = useColorModeValue("white", "#151517")
  const dragHandleBorderColor = useColorModeValue("gray.200", "gray.600")
  const dragHandleColor = useColorModeValue("gray.500", "gray.400")
  const moveButtonHoverBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const hasTransform = transform && (transform.x !== 0 || transform.y !== 0)
  const shouldShowPlaceholder = showPlaceholder
  const shouldShowContent = !showPlaceholder || hasTransform
  const appliedTransform = suppressTransform && !isDragging ? null : transform

  const style = {
    transform: appliedTransform ? CSS.Transform.toString(appliedTransform) : undefined,
    transition: isDragging && !suppressTransform ? transition : undefined,
    opacity: isDragging ? 0.6 : 1,
    cursor: editMode ? undefined : isDragging ? "grabbing" : "grab",
    zIndex: isDragging ? 1000 : 1,
    boxShadow: isDragging ? "0 8px 16px rgba(0, 0, 0, 0.2)" : "none",
  }

  const stopMovePointer = (event: React.PointerEvent | React.MouseEvent) => {
    event.stopPropagation()
  }

  const renderMoveButton = (
    direction: "up" | "down",
    disabled: boolean,
    onClick?: () => void,
    label?: string
  ) => (
    <Box
      as="button"
      type="button"
      aria-label={label}
      disabled={disabled}
      onPointerDown={stopMovePointer}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation()
        if (!disabled) onClick?.()
      }}
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="20px"
      h="20px"
      borderRadius="full"
      border="none"
      bg="transparent"
      color={dragHandleColor}
      opacity={disabled ? 0.35 : 1}
      cursor={disabled ? "not-allowed" : "pointer"}
      _hover={disabled ? undefined : { bg: moveButtonHoverBg }}
      flexShrink={0}
    >
      {direction === "up" ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
    </Box>
  )

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      w="100%"
      h={fillHeight ? "100%" : undefined}
      flex={fillHeight ? 1 : undefined}
      display={fillHeight ? "flex" : undefined}
      flexDirection={fillHeight ? "column" : undefined}
      minH={fillHeight ? 0 : undefined}
      position="relative"
    >
      {editMode ? (
        <Box
          data-dashboard-layout-card-drag-handle=""
          position="absolute"
          top="0"
          left="50%"
          transform="translateX(-50%)"
          zIndex={20}
          pointerEvents="auto"
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={showMoveArrows ? "2px" : undefined}
          w={showMoveArrows ? "auto" : "40px"}
          minW={showMoveArrows ? "72px" : undefined}
          px={showMoveArrows ? "2px" : undefined}
          h="24px"
          mt="-10px"
          borderRadius="full"
          bg={dragHandleBg}
          border="1px solid"
          borderColor={dragHandleBorderColor}
          color={dragHandleColor}
          boxShadow="sm"
        >
          {showMoveArrows ? renderMoveButton("up", !canMoveUp, onMoveUp, "Move widget up") : null}
          <Box
            {...listeners}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w={showMoveArrows ? "20px" : "100%"}
            h="100%"
            cursor={isDragging ? "grabbing" : "grab"}
            flexShrink={0}
          >
            <GripVertical size={14} aria-hidden />
          </Box>
          {showMoveArrows ? renderMoveButton("down", !canMoveDown, onMoveDown, "Move widget down") : null}
        </Box>
      ) : null}
      <Box
        style={style}
        w="100%"
        h={fillHeight ? "100%" : undefined}
        flex={fillHeight ? 1 : undefined}
        display={fillHeight ? "flex" : undefined}
        flexDirection={fillHeight ? "column" : undefined}
        minH={fillHeight ? 0 : undefined}
        position="relative"
        {...(!editMode ? listeners : {})}
      >
        {shouldShowPlaceholder && (
          <Box
            w="100%"
            minH="100%"
            border="2px dashed"
            borderColor={borderColor}
            borderRadius="6px"
            bg="transparent"
            pointerEvents="none"
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex={10}
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={
              hasTransform && appliedTransform
                ? {
                    transform: `translate(${-appliedTransform.x || 0}px, ${-appliedTransform.y || 0}px)`,
                  }
                : undefined
            }
          >
            <Text
              fontSize="sm"
              color={borderColor}
              fontWeight="medium"
              fontFamily="'IBM Plex Sans', sans-serif"
              textTransform="uppercase"
              opacity={0.6}
            >
              Drop here
            </Text>
          </Box>
        )}
        {shouldShowContent && (
          <Box
            position="relative"
            zIndex={1}
            flex={fillHeight ? 1 : undefined}
            display={fillHeight ? "flex" : undefined}
            flexDirection={fillHeight ? "column" : undefined}
            h={fillHeight ? "100%" : undefined}
            minH={fillHeight ? 0 : undefined}
            w="100%"
          >
            {children}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default SortableCard
